import actions from './actions';
import { SectionsActionTypes } from './types';
import { NotificationActionTypes } from '../notification/types';
import {
  getTableData,
  getSubmittedQualifications,
  getSubmittedAssessments,
  getSubmittedComponents,
  getSubmittedSections,
  getDownloadLink,
  getArchiveOfAllFiles,
  getSelectedFilesArchive,
} from '../../service/sections';
import { displayNotification } from '../notification/actions';
import { RootState } from '..';
import { BusinessUnit, Series, Qualification, Assessment, Component } from '@cambridgeassessment/lib-ee-commons';
import { WorkDownloadingParams } from '../../types/Sections';
import { downloadFromBlob, downloadFromUrl } from '../../util/downloadHandler';
import { SelectedBulkFilesDownloadBody } from '@cambridgeassessment/lib-ee-commons';

type Dispatch = (action: SectionsActionTypes | NotificationActionTypes) => void;

export const getQualifications =
  (businessUnitId: BusinessUnit['id'], seriesId: Series['id']) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const result = await getSubmittedQualifications(businessUnitId, seriesId);
      dispatch(actions.setQualifications(result));
    } catch (err) {
      dispatch(displayNotification('Failed to get Qualifications data'));
    }
  };

export const getAssessments =
  (businessUnitId: BusinessUnit['id'], seriesId: Series['id'], qualificationId: Qualification['id']) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const result = await getSubmittedAssessments(businessUnitId, seriesId, qualificationId);
      dispatch(actions.setAssessments(result));
    } catch (err) {
      dispatch(displayNotification('Failed to get Assessment data'));
    }
  };

export const getComponents =
  (
    businessUnitId: BusinessUnit['id'],
    seriesId: Series['id'],
    qualificationId: Qualification['id'],
    assessmentCode: Assessment['code'],
  ) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const result = await getSubmittedComponents(businessUnitId, seriesId, qualificationId, assessmentCode);
      dispatch(actions.setComponents(result));
    } catch (err) {
      dispatch(displayNotification('Failed to get Component data'));
    }
  };

export const getSections =
  (
    businessUnitId: BusinessUnit['id'],
    seriesId: Series['id'],
    qualificationId: Qualification['id'],
    assessmentCode: Assessment['code'],
    componentNumber: Component['number'],
  ) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const result = await getSubmittedSections(
        businessUnitId,
        seriesId,
        qualificationId,
        assessmentCode,
        componentNumber,
      );
      dispatch(actions.setSections(result));
    } catch (err) {
      dispatch(displayNotification('Failed to get Sections data'));
    }
  };

export const getTableResults =
  (
    businessUnitId: string,
    {
      seriesId,
      qualificationId,
      assessmentCode,
      componentNumber,
      sectionId,
      centreId,
      learnerNumber,
      isComponentDocument,
    }: WorkDownloadingParams,
    reset?: boolean,
    loadedSuccessfullyResolver?: () => void,
  ) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    try {
      const {
        sections: { fileResults, nextPageUrl },
      } = getState();

      dispatch(actions.setAreFilesLoading(true));

      if (reset) {
        dispatch(actions.setFilesTotal(0));
        dispatch(actions.setFilesNextPageUrl());
        dispatch(actions.setFileResults(undefined));
      }

      const { results, nextUrl, total } = await getTableData(
        businessUnitId,
        {
          seriesId,
          qualificationId,
          assessmentCode,
          componentNumber,
          sectionId,
          centreId,
          learnerNumber,
          isComponentDocument,
        },
        reset ? undefined : nextPageUrl,
      );
      const files = reset ? results : [...(fileResults || []), ...results];

      dispatch(actions.setAreFilesLoading(false));
      dispatch(actions.setFilesTotal(Number(total)));
      dispatch(actions.setFilesNextPageUrl(nextUrl));
      dispatch(actions.setFileResults(files));
      if (loadedSuccessfullyResolver) {
        loadedSuccessfullyResolver();
      }
    } catch (err) {
      dispatch(displayNotification('Failed to get Table data'));
      dispatch(actions.setAreFilesLoading(false));
    }
  };

export const openFile =
  (
    id: string,
    dispositionType: string,
    uniqId: string,
    target?: string,
    originalFileName?: string,
    isPartOfMultipleFiles?: boolean,
    isComponentDocument?: boolean,
  ) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    try {
      const {
        userData: { businessUnit },
      } = getState();

      const businessUnitId = businessUnit?.id;
      if (!businessUnitId) {
        return;
      }

      const { downloadUrl } = await getDownloadLink(
        businessUnitId,
        id,
        dispositionType,
        uniqId,
        originalFileName,
        isPartOfMultipleFiles,
        isComponentDocument,
      );
      downloadFromUrl(downloadUrl, undefined, target);
    } catch (error) {
      dispatch(displayNotification('Failed to get download URL'));
    }
  };

export const downloadAllFiles =
  (businessUnitId: string, params: WorkDownloadingParams, fileName: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.setIsDownloadFilesLoading(true));

      const archive = await getArchiveOfAllFiles(businessUnitId, params);

      downloadFromBlob(archive, fileName);
    } catch (error) {
      dispatch(displayNotification('Failed to download all files'));
    } finally {
      dispatch(actions.setIsDownloadFilesLoading(false));
    }
  };

export const downloadSelectedFiles =
  (businessUnitId: string, body: SelectedBulkFilesDownloadBody, fileName: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.setIsDownloadFilesLoading(true));

      const archive = await getSelectedFilesArchive(businessUnitId, body);

      downloadFromBlob(archive, fileName);
    } catch (error) {
      dispatch(displayNotification('Failed to download selected files'));
    } finally {
      dispatch(actions.setIsDownloadFilesLoading(false));
    }
  };
